import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import type { FC } from 'react'
import { Box, Grid, Divider, Radio } from '@mui/material'
import { Steerage, SteerageProvider, useUnselectSteerageProvider } from '~/entities/steerage'
import SteerageProviderListItem from './SteerageProviderListItem'
import { Loader } from '~/shared/ui'
import {
  referralSteerageProviders,
  type ReferralSteerageReasonForExceptionListResponse,
} from '@fireflyhealth/api'
import MemberSelectedView from './MemberSelectedView'
import { invalidateCachedCaseData } from '~/shared/lib/queryKeys'
import { fireAndForget } from '~/shared/lib/fireAndForget'

interface SteerageProps {
  steerage: Steerage
  steerageProviders?: Array<SteerageProvider> | null | undefined
  setShowEditProviderForm: (providerId: number | null) => void
  showEditProviderForm: number | null
  showAddProviderForm: boolean
  setShowAddProviderForm: (showAddProviderForm: boolean) => void
  validReasonsForException: ReferralSteerageReasonForExceptionListResponse
  isWaiverToggleEnabled?: boolean | null
  priorAuthorizationRequired?: boolean
  onSaveSteerage?: () => Promise<void>
}

export const SteerageProviderList: FC<SteerageProps> = props => {
  const steerage = props?.steerage
  const client = useQueryClient()
  const [memberSelectedProvider, setMemberSelectedProvider] = useState<number | null>(
    steerage?.steerage_providers?.find(provider => provider?.member_selected_at != null)?.id ??
      null,
  )

  //API's for selecting/unselecting the steerage provider on behalf of a member
  const { isPending: isUnselectingProvider, mutate: handleUnselectingSteerageProvider } =
    useUnselectSteerageProvider()

  const handleMemberSelectedProviderChange = () => {
    setMemberSelectedProvider(null)
    // unselecting the steerage provider
    handleUnselectingSteerageProvider(
      { id: props?.steerage?.id ?? 0 },
      {
        onSuccess: () =>
          fireAndForget('[Steerage] refresh after unselecting provider', async () => {
            // invalidate queries for refetching the providers
            await client.cancelQueries({
              queryKey: referralSteerageProviders.listQueryKey({
                steerage_id: props?.steerage?.id ?? 0,
              }),
            })
            await client.invalidateQueries({
              queryKey: referralSteerageProviders.listQueryKey({
                steerage_id: props?.steerage?.id ?? 0,
              }),
            })
            await invalidateCachedCaseData(client, props?.steerage?.person ?? null)
          }),
      },
    )
  }

  return (
    <Box sx={{ mt: 1 }}>
      {props?.steerageProviders && props?.steerageProviders?.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Divider />
        </Box>
      )}
      {props?.steerageProviders?.map(provider => (
        <SteerageProviderListItem
          key={provider?.id}
          steerage={steerage}
          provider={provider}
          setShowEditProviderForm={props.setShowEditProviderForm}
          showEditProviderForm={props.showEditProviderForm ?? null}
          showAddProviderForm={props.showAddProviderForm}
          setShowAddProviderForm={props.setShowAddProviderForm}
          validReasonsForException={props.validReasonsForException}
          memberSelectedProvider={memberSelectedProvider}
          setMemberSelectedProvider={setMemberSelectedProvider}
          isWaiverToggleEnabled={props?.isWaiverToggleEnabled}
          priorAuthorizationRequired={props.priorAuthorizationRequired}
          onSaveSteerage={props.onSaveSteerage}
        />
      ))}
      {/* Only show the radio buttons when it is locked and length of provider is greater then one */}
      {steerage?.is_locked && props?.steerageProviders && props?.steerageProviders?.length > 1 && (
        <Box sx={{ mt: 1, mb: 1 }}>
          {isUnselectingProvider ? <Loader /> : null}
          <Grid container>
            <Grid size={1}>
              <Radio
                checked={memberSelectedProvider ? false : true}
                value=""
                name="radio-buttons"
                onChange={handleMemberSelectedProviderChange}
              />
            </Grid>
            <Grid size={11}>
              <Box sx={{ mt: 1 }}>No provider is selected</Box>
            </Grid>
          </Grid>
          <Divider />
        </Box>
      )}

      {steerage?.is_locked && props?.steerageProviders && (
        <MemberSelectedView
          steerageProviders={props?.steerageProviders}
          steerage={props.steerage}
        />
      )}
    </Box>
  )
}
